<script setup>
// SiteUp: guia curto da navegação v2. Uma vez só (nav_v2_seen). Nunca prende.
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  inject,
} from 'vue';
import { useI18n } from 'vue-i18n';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import FocusSpotlight from './FocusSpotlight.vue';
import {
  useFocusSpotlight,
  findSpotTarget,
} from 'dashboard/composables/useFocusSpotlight';
import { useFocusMode } from 'dashboard/composables/useFocusMode';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { getModifierKey } from 'dashboard/composables/utils/useKbd';
import { legacySidebarMap } from 'dashboard/routes/dashboard/hub/hubWorlds';

const TOUR_STEPS = [
  {
    id: 'navChanged',
    selectors: [
      '[data-testid="focus-mode-hub"]',
      '[data-testid="mobile-nav-hub"]',
    ],
    goToHub: false,
  },
  {
    id: 'listMode',
    selectors: ['[data-testid="focus-mode-menu-toggle"]'],
    goToHub: false,
  },
  {
    id: 'attendance',
    selectors: [
      '[data-testid="hub-world-attendance"]',
      '[data-testid="focus-mode-hub"]',
      '[data-testid="mobile-nav-hub"]',
    ],
    goToHub: true,
  },
  {
    id: 'commandK',
    selectors: ['[data-testid="hub-search"]'],
    goToHub: true,
  },
];

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { markWelcomeSeen, markTourCompleted, markNavV2Seen, saveTourStep } =
  useFocusMode();
const host = inject('focusModeGuideHost', null);
const { spotlightStyle, setSelector, measure, attach, detach } =
  useFocusSpotlight();

const showWelcome = ref(false);
const showTour = ref(false);
const stepIndex = ref(0);
const cardRef = ref(null);
const balloonRef = ref(null);

let previousFocus = null;
let ignoreOutsideClick = false;
let onDocKeydown = null;
let onDocClick = null;
let cycleTimer = null;

function stepTargets(step) {
  if (!step) return [];
  const list = step.selectors || (step.selector ? [step.selector] : []);
  return list.filter(selector => findSpotTarget(selector));
}

function stepIsReachable(step) {
  if (!step) return false;
  if (step.openMenu) return true;
  return stepTargets(step).length > 0;
}

function nextValidIndex(from, dir) {
  let index = from + dir;
  while (index >= 0 && index < TOUR_STEPS.length) {
    if (stepIsReachable(TOUR_STEPS[index])) return index;
    index += dir;
  }
  return -1;
}

function stopCycle() {
  if (!cycleTimer) return;
  window.clearTimeout(cycleTimer);
  window.clearInterval(cycleTimer);
  cycleTimer = null;
}

function waitForTarget(selector, attempts = 12) {
  return new Promise(resolve => {
    const tick = left => {
      if (findSpotTarget(selector) || left <= 0) {
        resolve(Boolean(findSpotTarget(selector)));
        return;
      }
      window.setTimeout(() => tick(left - 1), 50);
    };
    tick(attempts);
  });
}

const currentStep = computed(() => TOUR_STEPS[stepIndex.value] || null);
const isLastStep = computed(() => {
  const next = nextValidIndex(stepIndex.value, 1);
  return next < 0;
});

const stepTitle = computed(() => {
  const step = currentStep.value;
  if (!step) return '';
  if (step.id === 'navChanged') return t('HUB.NAV_V2.STEP_1_TITLE');
  if (step.id === 'listMode') return t('HUB.NAV_V2.STEP_4_TITLE');
  if (step.id === 'attendance') return t('HUB.NAV_V2.STEP_2_TITLE');
  return t('HUB.NAV_V2.STEP_3_TITLE', { mod: getModifierKey() });
});

const stepBody = computed(() => {
  const step = currentStep.value;
  if (!step) return '';
  if (step.id === 'navChanged') return t('HUB.NAV_V2.STEP_1_BODY');
  if (step.id === 'listMode') return t('HUB.NAV_V2.STEP_4_BODY');
  if (step.id === 'attendance') return t('HUB.NAV_V2.STEP_2_BODY');
  return t('HUB.NAV_V2.STEP_3_BODY');
});

const showWhereMap = ref(false);

const whereGroups = computed(() => {
  const groups = [];
  const index = new Map();
  legacySidebarMap().forEach(item => {
    let group = index.get(item.worldId);
    if (!group) {
      group = {
        id: item.worldId,
        label: t(item.worldLabelKey),
        items: [],
      };
      index.set(item.worldId, group);
      groups.push(group);
    }
    group.items.push({
      id: item.id,
      label: t(item.labelKey),
    });
  });
  return groups;
});

const stepLabel = computed(
  () => `${stepIndex.value + 1} / ${TOUR_STEPS.length}`
);

const isGuideOpen = computed(() => showWelcome.value || showTour.value);

const firstValidIndex = () =>
  TOUR_STEPS.findIndex(step => stepIsReachable(step));

const restoreFocus = () => {
  if (previousFocus && typeof previousFocus.focus === 'function') {
    previousFocus.focus();
  }
  previousFocus = null;
};

const detachDoc = () => {
  if (typeof document === 'undefined') return;
  document.removeEventListener('keydown', onDocKeydown);
  document.removeEventListener('click', onDocClick);
};

const focusCard = () => {
  nextTick(() => {
    const root = showTour.value ? balloonRef.value : cardRef.value;
    root?.querySelector('button')?.focus();
  });
};

const syncSpot = selector => {
  setSelector(selector);
  return measure();
};

function finishTour() {
  stopCycle();
  showTour.value = false;
  showWhereMap.value = false;
  host?.closeMenu?.();
  detach();
  setSelector('');
  markTourCompleted();
  markNavV2Seen();
  detachDoc();
  restoreFocus();
}

const ensureHub = async () => {
  if (router.currentRoute.value.name === 'focus_hub') return;
  await router.replace(accountScopedRoute('focus_hub'));
  await waitForTarget('[data-testid="hub-search"]');
};

const paintTargets = targets => {
  const paint = selector => {
    attach([selector, '[data-testid="focus-mode-cluster"]']);
    syncSpot(selector);
  };
  paint(targets[0]);
  if (targets.length < 2) return;
  cycleTimer = window.setTimeout(() => {
    cycleTimer = null;
    paint(targets[1]);
  }, 1600);
};

const applyStep = index => {
  stopCycle();
  showWhereMap.value = false;
  const step = TOUR_STEPS[index];
  if (!step) {
    finishTour();
    return;
  }
  host?.closeMenu?.();
  stepIndex.value = index;
  saveTourStep(index);
  nextTick(async () => {
    if (step.goToHub) await ensureHub();
    if (step.selector) await waitForTarget(step.selector, 16);
    const targets = stepTargets(step);
    if (!targets.length) {
      const fallback = nextValidIndex(index, 1);
      if (fallback < 0) {
        finishTour();
        return;
      }
      applyStep(fallback);
      return;
    }
    paintTargets(targets);
    focusCard();
    nextTick(() => syncSpot(targets[0]));
  });
};

function dismissWelcome() {
  showWelcome.value = false;
  detach();
  setSelector('');
  markWelcomeSeen();
  markNavV2Seen();
  detachDoc();
  restoreFocus();
}

function dismissTour() {
  stopCycle();
  showTour.value = false;
  showWhereMap.value = false;
  host?.closeMenu?.();
  detach();
  setSelector('');
  markWelcomeSeen();
  markNavV2Seen();
  saveTourStep(stepIndex.value);
  detachDoc();
  restoreFocus();
}

function closeGuide() {
  if (showTour.value) {
    dismissTour();
    return;
  }
  if (showWelcome.value) dismissWelcome();
}

onDocKeydown = event => {
  if (!isGuideOpen.value || event.key !== 'Escape') return;
  event.preventDefault();
  closeGuide();
};

onDocClick = event => {
  if (!isGuideOpen.value || ignoreOutsideClick) return;
  const card = showTour.value ? balloonRef.value : cardRef.value;
  if (card?.contains(event.target)) return;
  const selectors = showTour.value
    ? stepTargets(currentStep.value)
    : ['[data-testid="focus-mode-cluster"]'];
  const insideTarget = selectors.some(selector =>
    document.querySelector(selector)?.contains(event.target)
  );
  if (insideTarget) return;
  closeGuide();
};

const attachDoc = () => {
  if (typeof document === 'undefined') return;
  detachDoc();
  document.addEventListener('keydown', onDocKeydown);
  document.addEventListener('click', onDocClick);
};

const startTour = async () => {
  showWelcome.value = false;
  markWelcomeSeen();
  previousFocus = previousFocus || document.activeElement;
  if (
    !findSpotTarget('[data-testid="focus-mode-hub"]') &&
    !findSpotTarget('[data-testid="mobile-nav-hub"]')
  ) {
    await ensureHub();
  }
  const start = firstValidIndex();
  if (start < 0) {
    markWelcomeSeen();
    markNavV2Seen();
    return;
  }
  showTour.value = true;
  ignoreOutsideClick = true;
  applyStep(start);
  attachDoc();
  if (typeof window === 'undefined') return;
  window.setTimeout(() => {
    ignoreOutsideClick = false;
  }, 80);
};

const goNext = () => {
  const next = nextValidIndex(stepIndex.value, 1);
  if (next < 0) {
    finishTour();
    return;
  }
  applyStep(next);
};

const goBack = () => {
  const prev = nextValidIndex(stepIndex.value, -1);
  if (prev < 0) return;
  applyStep(prev);
};

// Aposentado em 23/08/2026. Este guia disparava sozinho e colidia com o
// onboarding novo: o usuario via o card da dica e este balao ao mesmo tempo,
// e o contador ainda comecava em "2 / 4" porque passo sem alvo era pulado sem
// corrigir o total. O que ele ensinava (hub, modos de lista, Cmd+K) agora esta
// na dica por tela, no manual e no tour guiado, que tem retomada e replay.
// O componente segue montado e continua abrindo pelo botao "Rever o guia" das
// preferencias do Modo Foco, so nao se convida mais sozinho.
onMounted(() => {
  markNavV2Seen();
});

onBeforeUnmount(() => {
  stopCycle();
  detachDoc();
  detach();
});

defineExpose({ startTour });
</script>

<template>
  <TeleportWithDirection to="body">
    <template v-if="isGuideOpen">
      <FocusSpotlight :style-value="spotlightStyle" />

      <div
        v-if="showWelcome"
        ref="cardRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="focus-mode-welcome-title"
        class="fixed left-1/2 top-1/2 z-[80] w-72 max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-visible rounded-2xl border border-n-weak bg-n-solid-2 p-4 shadow-xl"
        data-testid="focus-mode-welcome"
        @click.stop
      >
        <p
          id="focus-mode-welcome-title"
          class="m-0 mb-1 text-base font-semibold text-n-slate-12"
        >
          {{ t('SIDEBAR.FOCUS_MODE.WELCOME_TITLE') }}
        </p>
        <p class="m-0 mb-3 text-sm leading-relaxed text-n-slate-11">
          {{ t('SIDEBAR.FOCUS_MODE.WELCOME_BODY') }}
        </p>
        <div class="flex flex-col items-stretch gap-2">
          <button
            type="button"
            class="min-h-9 w-full rounded-full bg-n-slate-12 px-4 py-2 text-sm font-semibold leading-snug text-n-background hover:opacity-90"
            data-testid="focus-mode-welcome-tour"
            @click="startTour"
          >
            {{ t('SIDEBAR.FOCUS_MODE.WELCOME_TOUR') }}
          </button>
          <button
            type="button"
            class="min-h-9 w-full rounded-full px-3 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="focus-mode-welcome-later"
            @click="dismissWelcome"
          >
            {{ t('SIDEBAR.FOCUS_MODE.WELCOME_LATER') }}
          </button>
        </div>
      </div>

      <div
        v-else-if="showTour"
        ref="balloonRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="focus-mode-tour-title"
        class="fixed left-1/2 top-1/2 z-[80] flex h-[min(22rem,calc(100vh-4rem))] w-72 max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-2xl border border-n-weak bg-n-solid-2 p-4 shadow-xl"
        data-testid="focus-mode-tour"
        :data-step="currentStep?.id"
        @click.stop
      >
        <p
          class="m-0 mb-1 text-[11px] font-semibold tabular-nums text-n-iris-11"
        >
          {{ stepLabel }}
        </p>
        <p
          id="focus-mode-tour-title"
          class="m-0 mb-1 text-sm font-semibold text-n-slate-12"
        >
          {{ stepTitle }}
        </p>
        <p class="m-0 mb-3 text-sm leading-relaxed text-n-slate-11">
          {{ stepBody }}
        </p>
        <button
          v-if="isLastStep"
          type="button"
          class="mb-3 self-start text-left text-xs font-medium text-n-slate-11 underline-offset-2 hover:text-n-slate-12 hover:underline"
          data-testid="focus-mode-tour-where"
          @click="showWhereMap = !showWhereMap"
        >
          {{ t('HUB.NAV_V2.WHERE_LINK') }}
        </button>
        <div
          v-if="isLastStep && showWhereMap"
          class="mb-3 max-h-40 overflow-y-auto rounded-xl border border-n-weak bg-n-solid-1 p-2"
          data-testid="focus-mode-tour-where-map"
        >
          <p class="m-0 mb-2 text-[11px] font-semibold text-n-slate-11">
            {{ t('HUB.NAV_V2.WHERE_TITLE') }}
          </p>
          <div
            v-for="group in whereGroups"
            :key="group.id"
            class="mb-2 last:mb-0"
          >
            <p class="m-0 mb-1 text-[11px] font-bold text-n-slate-12">
              {{ group.label }}
            </p>
            <ul class="m-0 list-none space-y-0.5 p-0">
              <li
                v-for="item in group.items"
                :key="item.id"
                class="text-[11px] leading-snug text-n-slate-11"
              >
                {{ item.label }}
              </li>
            </ul>
          </div>
        </div>
        <div class="mt-auto flex items-center justify-between gap-1">
          <button
            type="button"
            class="min-h-9 shrink-0 rounded-full px-3 text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="focus-mode-tour-skip"
            @click="dismissTour"
          >
            {{ t('SIDEBAR.FOCUS_MODE.TOUR_SKIP') }}
          </button>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="min-h-9 rounded-full px-3 text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
              data-testid="focus-mode-tour-back"
              :disabled="nextValidIndex(stepIndex, -1) < 0"
              @click="goBack"
            >
              {{ t('SIDEBAR.FOCUS_MODE.TOUR_BACK') }}
            </button>
            <button
              type="button"
              class="min-h-9 rounded-full bg-n-slate-12 px-3 text-xs font-medium text-n-background hover:opacity-90"
              data-testid="focus-mode-tour-next"
              @click="goNext"
            >
              {{
                isLastStep
                  ? t('SIDEBAR.FOCUS_MODE.TOUR_DONE')
                  : t('SIDEBAR.FOCUS_MODE.TOUR_NEXT')
              }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </TeleportWithDirection>
</template>
